import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import Brand from "@/components/common/Brand";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  loginAdmin,
  seedAdminUsers,
  isAdminAuthenticated,
} from "@/data/adminAuthStore";
import { ADMIN_AUTH } from "@/constants/testIds";

/**
 * Pantalla sobria de login administrativo. Sin recuperación de contraseña,
 * sin registro público. Usuarios provisionales sembrados en localStorage.
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    seedAdminUsers();
  }, []);

  // Destino post-login: respeta `?next=` (usado por guards de recepción)
  // además del clásico `location.state.from`.
  const nextParam = new URLSearchParams(location.search).get("next");
  const destinoPostLogin = nextParam || (location.state && location.state.from) || "/admin";

  if (isAdminAuthenticated()) {
    return <Navigate to={destinoPostLogin} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!usuario.trim() || !password) {
      setError("Ingresa usuario y contraseña.");
      return;
    }
    setLoading(true);
    const session = await loginAdmin(usuario, password);
    setLoading(false);
    if (!session) {
      setError("Usuario o contraseña incorrectos.");
      return;
    }
    if (session.error === "network") {
      setError("No se pudo conectar con el servidor. Intenta más tarde.");
      return;
    }
    navigate(destinoPostLogin, { replace: true });
  };

  return (
    <div
      data-testid={ADMIN_AUTH.screen}
      className="min-h-[100dvh] bg-background text-foreground flex flex-col"
    >
      <header className="w-full border-b hairline">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Brand />
          <span className="overline text-muted-foreground">
            Portal administrativo
          </span>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-primary/5 dark:bg-accent/10 ring-1 ring-accent/30 grid place-items-center mb-5 text-primary dark:text-accent">
              <Lock className="w-5 h-5" strokeWidth={1.6} />
            </div>
            <span className="overline text-accent">CT Connect · Consola</span>
            <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight">
              Acceso administrativo
            </h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-sm leading-relaxed">
              Ingresa con tu usuario institucional para operar el portal del
              Casino Tampiqueño.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="rounded-2xl border hairline bg-card p-6 md:p-8 shadow-soft flex flex-col gap-5"
            noValidate
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="admin-usuario">Usuario</Label>
              <Input
                id="admin-usuario"
                data-testid={ADMIN_AUTH.inputUsuario}
                autoComplete="username"
                autoCapitalize="off"
                spellCheck={false}
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                placeholder="usuario"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="admin-password">Contraseña</Label>
              <Input
                id="admin-password"
                data-testid={ADMIN_AUTH.inputPassword}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p
                data-testid={ADMIN_AUTH.errorMsg}
                className="text-sm text-destructive"
                role="alert"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid={ADMIN_AUTH.btnEntrar}
              disabled={loading}
              className="mt-2 h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? "Verificando…" : "Iniciar sesión"}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Uso interno · Casino Tampiqueño
          </p>
        </div>
      </main>
    </div>
  );
}
